import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ROLES } from '../constants/roles';
import { isSessionInvalidStatus, loadSessionUser } from '../utils/sessionLoad';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [activeCompanyId, setActiveCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionLoadError, setSessionLoadError] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setCompany(null);
    setActiveCompanyId(null);
    setSessionLoadError(false);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const applySessionPayload = useCallback((data) => {
    setUser(data.user);
    setCompany(data.company ?? null);
    setActiveCompanyId(data.activeCompanyId);
    setSessionLoadError(false);
  }, []);

  const fetchUserData = useCallback(async () => {
    setLoading(true);
    setSessionLoadError(false);
    const result = await loadSessionUser(() => axios.get('/api/auth/me'));
    if (result.ok) {
      applySessionPayload(result.data);
    } else if (result.authInvalid) {
      console.error('Session invalid — logging out:', result.error);
      logout();
    } else {
      // Keep the token so a refresh/retry can recover; do not treat blips as logout.
      console.error('Failed to fetch user data (keeping session):', result.error);
      setUser(null);
      setCompany(null);
      setActiveCompanyId(null);
      setSessionLoadError(true);
    }
    setLoading(false);
  }, [applySessionPayload, logout]);

  // Set up axios interceptor; run only when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserData();
    } else {
      setLoading(false);
      setSessionLoadError(false);
    }
  }, [token, fetchUserData]);

  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password });
      if (response.data.requiresMfa) {
        return {
          success: false,
          requiresMfa: true,
          mfaToken: response.data.mfaToken,
        };
      }
      const { token: newToken, user: userData, company: companyData, activeCompanyId: activeId } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      setCompany(companyData ?? null);
      setActiveCompanyId(activeId);
      setSessionLoadError(false);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
      };
    }
  };

  const verifyMfa = async (mfaToken, code) => {
    try {
      const response = await axios.post('/api/auth/verify-mfa', { mfaToken, code });
      const { token: newToken, user: userData, company: companyData, activeCompanyId: activeId } = response.data;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      setCompany(companyData ?? null);
      setActiveCompanyId(activeId);
      setSessionLoadError(false);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Invalid or expired code',
      };
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      await axios.post('/api/auth/forgot-password', { email });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Request failed',
      };
    }
  };

  const mfaEnable = async () => {
    try {
      await axios.post('/api/auth/mfa/enable');
      const response = await axios.get('/api/auth/me');
      setUser(response.data.user);
      setCompany(response.data.company ?? null);
      setActiveCompanyId(response.data.activeCompanyId);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to enable 2FA',
      };
    }
  };

  const mfaDisable = async (password) => {
    try {
      await axios.post('/api/auth/mfa/disable', { password });
      const response = await axios.get('/api/auth/me');
      setUser(response.data.user);
      setCompany(response.data.company ?? null);
      setActiveCompanyId(response.data.activeCompanyId);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to disable 2FA',
      };
    }
  };

  const updateUserLanguage = (language) => {
    setUser((prev) => (prev ? { ...prev, language } : prev));
  };

  const refreshSession = useCallback(async () => {
    if (!token) return { success: false };
    try {
      const response = await axios.get('/api/auth/me');
      setUser((prev) => (prev ? { ...prev, ...response.data.user } : response.data.user));
      setCompany(response.data.company ?? null);
      setActiveCompanyId(response.data.activeCompanyId);
      setSessionLoadError(false);
      return { success: true };
    } catch (error) {
      console.error('Failed to refresh session:', error);
      if (isSessionInvalidStatus(error.response?.status)) {
        logout();
      }
      return { success: false };
    }
  }, [token, logout]);

  const retrySessionLoad = useCallback(() => {
    if (!token) return;
    fetchUserData();
  }, [token, fetchUserData]);

  const activeCompany = company;
  const userRole = user?.role;
  const isClient = userRole === ROLES.CLIENT;
  const isInternal = !isClient && (userRole === ROLES.MANAGER || userRole === ROLES.EMPLOYEE);
  const customPermissions = user?.customPermissions || [];
  const hasStoredSession = Boolean(token);

  const value = {
    user,
    company,
    activeCompanyId,
    activeCompany,
    isInternal,
    isClient,
    userRole,
    customPermissions,
    loading,
    hasStoredSession,
    sessionLoadError,
    login,
    verifyMfa,
    requestPasswordReset,
    mfaEnable,
    mfaDisable,
    logout,
    updateUserLanguage,
    refreshSession,
    retrySessionLoad,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
